import { showsMarkingTools, showsMyDrafts } from '../MainSidebar/navItems';

/**
 * Which school tours a user may be offered.
 *
 * The marking and draft tours open on a tip rendered INSIDE a mirror-sidebar
 * item (Marking Tools / My Drafts, components-onboarding/MainSidebar). The
 * mirror sidebar shows those items only when the user's real sidebar does, so
 * a tour is offered only when the item it points at is there. Otherwise its
 * first tip never mounts and the user sits on a dimmed, click-locked screen
 * until TourGuard's stranded-tour watchdog gives up.
 *
 * `role` and `permissions` are what the mirror sidebar itself reads:
 * getUserRole() and profile.permissions.
 */
export const isTourAvailable = (tourName, role, permissions) => {
  if (tourName === 'marking') return showsMarkingTools(role, permissions);
  if (tourName === 'draft') return showsMyDrafts(permissions);
  return true;
};

const SETUP_TASK_COUNT = 8;

// The setup-checklist row each gated tour completes (onboardingTasks index;
// TourContext's MARK_TOUR_COMPLETE uses the same indices).
const TOUR_TASK_INDEX = { marking: 3, draft: 4 };

export const ALL_SETUP_TASKS_APPLICABLE = Array(SETUP_TASK_COUNT).fill(true);

/**
 * Per checklist row: whether it applies to this user. A row whose tour is not
 * available is not offered, and Setup Progress counts only applicable rows,
 * so such a user can still reach 100%.
 */
export const applicableSetupTasks = (role, permissions) => {
  const applicable = [...ALL_SETUP_TASKS_APPLICABLE];
  Object.entries(TOUR_TASK_INDEX).forEach(([tourName, index]) => {
    applicable[index] = isTourAvailable(tourName, role, permissions);
  });
  return applicable;
};

/**
 * A persisted tour snapshot (jeddle_tour_state_<uid>) with any tour this user
 * can no longer be offered taken out. The user is put back on the setup
 * checklist instead, as the stranded-tour watchdog would do.
 */
export const withoutUnavailableTours = (snapshot, role, permissions) => {
  if (!snapshot || typeof snapshot !== 'object') return snapshot;
  const next = { ...snapshot };
  let dropped = false;
  if (
    next.markingTourActive &&
    !isTourAvailable('marking', role, permissions)
  ) {
    next.markingTourActive = false;
    next.markingTourStep = 1;
    dropped = true;
  }
  if (next.draftTourActive && !isTourAvailable('draft', role, permissions)) {
    next.draftTourActive = false;
    next.draftTourStep = 1;
    next.showDraftFeedback = false;
    dropped = true;
  }
  if (dropped) next.showOnboardingComplete = true;
  return next;
};
